/**
 * Employee availability reads (#2041). Employees write their own availability in the
 * employee app; the web reads it for the planner grid and the employee page. Both routes
 * are shifts-absences.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { useModuleGate } from '@/hooks/useAccess';
import { EmployeesService } from '@/api/services/EmployeesService';
import { ApiError } from '@/api/core/ApiError';
import type { AvailabilityRange, EmployeeAvailability } from '@/types/availability';

const AVAILABILITY_READ_MODULES = ['shifts-absences'] as const;

export const availabilityKeys = {
  all: ['employeeAvailability'] as const,
  range: (from: string, to: string) => [...availabilityKeys.all, 'range', { from, to }] as const,
  employee: (employeeId: string) => [...availabilityKeys.all, 'employee', employeeId] as const,
};

const retryUnlessClientError = (failureCount: number, error: unknown) => {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 3;
};

/** Every employee's rules and blocked days in [from, to] (at most 62 days). */
export function useAvailabilityRange(from: string, to: string, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  const canRead = useModuleGate(AVAILABILITY_READ_MODULES);

  return useQuery({
    queryKey: availabilityKeys.range(from, to),
    queryFn: (): Promise<AvailabilityRange> => EmployeesService.getApiV1EmployeeAvailability(from, to),
    enabled: (options?.enabled ?? true) && !!from && !!to && !!isSignedIn && isAuthReady && canRead,
    staleTime: 2 * 60 * 1000,
    retry: retryUnlessClientError,
  });
}

/** One employee's current weekly set and blocked days from today (employee page). */
export function useEmployeeAvailability(employeeId: string | undefined, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: availabilityKeys.employee(employeeId ?? ''),
    queryFn: (): Promise<EmployeeAvailability> => EmployeesService.getApiV1EmployeesAvailability(employeeId ?? ''),
    enabled: (options?.enabled ?? true) && !!employeeId && !!isSignedIn && isAuthReady,
    staleTime: 60 * 1000,
    retry: retryUnlessClientError,
  });
}
